import type { ChatConfig, ApiResponse, WidgetConfig, Message } from '@/types';

export class ApiService {
  private baseUrl: string;
  private apiKey: string;

  constructor(config: WidgetConfig) {
    this.baseUrl = config.serverUrl || 'https://apibusi.chatcarteldev.com/api/v1';
    this.apiKey = config.apiKey;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const defaultHeaders = {
      'Authorization': `Bearer ${this.apiKey}`,
      'Content-Type': 'application/json'
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }


  async loadWebMessages(clientUuid: string, page: number = 1, limit: number = 100): Promise<Message[]> {
    try {
      // console.log('📚 Cargando mensajes del backend:', { clientUuid, page, limit });

      const response = await fetch(`${this.baseUrl}/messages/list-web-messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`, // API key del cliente
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientUuid: clientUuid,
          page: page.toString(),
          limit: limit.toString()
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('API Key inválido o expirado');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // console.log('✅ Respuesta de mensajes:', data);

      // Transformar mensajes del backend al formato interno
      const messages = data.messages || data.data || [];

      const transformedMessages = messages.map((msg: any) => {


        if (msg.Of === 'Bot' && msg.Type === 'BUTTON') {
          // console.log('🤖 Mensaje tipo BUTTON del bot detectado:', msg);

          const header =  msg.Phrase?.header.type === 'text'
            ? msg.Phrase.header.text
            : msg.Phrase?.header || '';

          return {
            id: msg.ID || msg.id || Date.now().toString(),
            text: msg.Phrase?.body || '',
            sender: 'bot' as const,
            type: 'button' as const,
            timestamp: new Date(msg.Date || msg.timestamp || Date.now()),
            senderType: 'Bot', // AGREGAR
            botData: {
              header: header,
              body: msg.Phrase?.body || '',
              footer: msg.Phrase?.footer || '',
              buttons: msg.Phrase?.buttons || []
            }
          };
        }

        if (msg.Of === 'Bot' && msg.Type === 'LIST') {
          // console.log('🤖 Mensaje tipo LIST del bot detectado:', msg);

          return {
            id: msg.ID || msg.id || Date.now().toString(),
            text: msg.Phrase?.body || '',
            sender: 'bot' as const,
            type: 'list' as const,
            timestamp: new Date(msg.Date || msg.timestamp || Date.now()),
            senderType: 'Bot', // AGREGAR
            botData: {
              header: msg.Phrase?.header || '',
              body: msg.Phrase?.body || '',
              footer: msg.Phrase?.footer || '',
              buttons: msg.Phrase?.buttons || [],
              buttonText: msg.Phrase?.buttonText || ''
            }
          };
        }


        // NUEVO: Detectar mensajes de sistema (Transferencia y Finalización)
        if (msg.Template === 'Transference') {
          // console.log('🔄 Mensaje de transferencia detectado:', msg);

          return {
            id: msg.ID || msg.id || Date.now().toString(),
            text: `Su conversación se transfirió a ${msg.Receiver || 'Agente'}`,
            sender: 'system' as const,
            type: 'transfer' as const,
            timestamp: new Date(msg.Date || msg.timestamp || Date.now()),
            agentName: msg.Receiver,
            description: msg.Description
          };
        }

        if (msg.Template === 'Finished') {
          // console.log('✅ Mensaje de finalización detectado:', msg);

          return {
            id: msg.ID || msg.id || Date.now().toString(),
            // text: `Su conversación fue finalizada por el agente: ${msg.User || 'Agente'}`,
            text: `Su conversación fue finalizada`,
            sender: 'system' as const,
            type: 'finished' as const,
            timestamp: new Date(msg.Date || msg.timestamp || Date.now()),
            agentName: msg.User,
            description: msg.Description
          };
        }

        // Resto del código existente para mensajes normales
        let messageType: 'text' | 'image' | 'file' | 'system' = 'text';
        let messageText = '';
        let fileUrl = '';
        let fileName = '';
        let caption = '';
        const senderType = msg.Of === 'Bot' ? 'Bot' :
          msg.Of === 'Operador' ? 'Operador' :
            undefined;

        if (msg.Type === 'IMAGE' || msg.type === 'IMAGE') {
          messageType = 'image';
          fileUrl = msg.Phrase || msg.fileUrl || msg.url || '';
          caption = msg.Caption || msg.caption || '';
          messageText = caption || 'Imagen';

          if (fileUrl) {
            try {
              const urlParts = fileUrl.split('/');
              fileName = urlParts[urlParts.length - 1] || 'imagen.jpg';
            } catch (e) {
              fileName = 'imagen.jpg';
            }
          }

        } else if (msg.Type === 'DOCUMENT' || msg.type === 'DOCUMENT') {
          messageType = 'file';
          fileUrl = msg.Phrase || msg.fileUrl || msg.url || '';
          caption = msg.Caption || msg.caption || '';
          messageText = caption || 'Documento';

          if (fileUrl) {
            try {
              const urlParts = fileUrl.split('/');
              // fileName = urlParts[urlParts.length - 1] || 'documento.pdf';
              fileName = 'documento.pdf';
            } catch (e) {
              fileName = 'documento.pdf';
            }
          }

        } else {
          messageText = msg.message?.text || msg.text || msg.Phrase || '';
        }

        return {
          id: msg.uuid || msg.ID || msg.id || Date.now().toString(),
          text: messageText,
          sender: this.determineSender(msg),
          type: messageType,
          timestamp: new Date(msg.createdAt || msg.Date || msg.timestamp || Date.now()),
          senderType: senderType, // AGREGAR
          ...(fileUrl && { fileUrl }),
          ...(fileName && { fileName }),
          ...(caption && { caption })
        };
      }).sort((a: Message, b: Message) => a.timestamp.getTime() - b.timestamp.getTime());

      // console.log(`✅ ${transformedMessages.length} mensajes transformados correctamente`);

      return transformedMessages;

    } catch (error) {
      console.error('❌ Error cargando mensajes del backend:', error);
      throw error;
    }
  }


  private determineSender(msg: any): 'user' | 'agent' |'bot'| 'system' {

    if(msg.Of === 'Cliente') {
      return 'user';
    }
    else if(msg.Of === 'Bot') {
      return 'bot';
    }else if (msg.Of === 'Operador') {
      return 'agent';
    }else{
      return 'system';
    }

    // Determinar el sender basado en la estructura del mensaje
    if (msg.sender === 'user' || msg.Of === 'Cliente' || msg.isFromClient || msg.direction === 'outbound') {
      return 'user';
    } else if (msg.sender === 'agent' || msg.Of === 'Operador' || msg.Of === 'Bot' || msg.isFromAgent || msg.direction === 'inbound') {
      return 'agent';
    } else {
      return 'system';
    }
  }





  /**
  * Enviar mensaje al backend
  */
  async sendWebMessage(visitorName: string, visitorId: string, messageText: string): Promise<any> {
    const messagePayload = {
      clientData: {
        name: visitorName,
        uuid: visitorId
      },
      messageData: {
        id: Date.now().toString(),
        type: "text",
        text: {
          body: messageText
        }
      }
    };

    // console.log('📤 Enviando a /messages/web:', messagePayload);

    try {
      const response = await fetch(`${this.baseUrl}/messages/web`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messagePayload)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('API Key inválido o expirado');
        }else if (response.status === 404) {
           this.deleleteDataVisitor()
          throw new Error('Widget ya no existe o fue eliminado');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      // console.log('✅ Respuesta del servidor:', result);
      return result;

    } catch (error) {
      console.error('❌ Error enviando mensaje:', error);
      throw error;
    }
  }


  deleleteDataVisitor(){
    localStorage.removeItem('zimpli_client_uuid');
    localStorage.removeItem('zimpli_client_name');
    localStorage.removeItem('zimpli_client_email');
  }

  async sendWebMessageWithFile(
    visitorName: string,
    visitorId: string,
    file: File,
    caption?: string
  ): Promise<any> {
    try {
      // 1️⃣ Convertir el archivo a Base64
      const base64 = await this.convertFileToBase64(file);

      // 2️⃣ Armar el mismo payload que el mensaje de texto, pero con tipo 'image' o 'document'
      const messagePayload = {
        clientData: {
          name: visitorName,
          uuid: visitorId
        },
        messageData: {
          id: Date.now().toString(),
          type: file.type.startsWith('image/') ? 'image' : 'document',
          [file.type.startsWith('image/') ? 'image' : 'document']: {
            caption: caption || '',
            filename: file.name,
            mimetype: file.type,
            data: base64.split(',')[1] // Solo la parte del contenido, sin "data:image/png;base64,"
          }
        }
      };

      // console.log('📤 Enviando mensaje con archivo:', messagePayload);

      // 3️⃣ Enviar como JSON normal
      const response = await fetch(`${this.baseUrl}/messages/web`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messagePayload)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('API Key inválido o expirado');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      // console.log('✅ Archivo enviado correctamente:', result);
      return result;

    } catch (error) {
      console.error('❌ Error enviando archivo:', error);
      throw error;
    }
  }

  // Función auxiliar para convertir a Base64
  private convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }



  /**
  * Obtener contador de mensajes no leídos
  */
  async getUnreadMessagesCount(visitorId: string): Promise<number> {
    try {
      // console.log('🔢 Verificando mensajes no leídos para:', visitorId);

      const response = await fetch(`${this.baseUrl}/messages/web/counter/${visitorId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('API Key inválido o expirado');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      const count = data.data.count || data.data.unreadCount || data.count || data.unreadCount || data.counter || 0;

      // console.log('📊 Mensajes no leídos:', count);
      return count;

    } catch (error) {
      console.error('❌ Error obteniendo contador de mensajes:', error);
      return 0; // Retornar 0 en caso de error
    }
  }

  /**
   * Marcar mensajes como leídos
   */
  async markMessagesAsRead(visitorId: string): Promise<boolean> {
    try {
      // console.log('✅ Marcando mensajes como leídos para:', visitorId);

      const response = await fetch(`${this.baseUrl}/messages/web/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientUuid: visitorId
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('API Key inválido o expirado');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      // console.log('✅ Mensajes marcados como leídos:', result);
      return true;

    } catch (error) {
      console.error('❌ Error marcando mensajes como leídos:', error);
      return false;
    }
  }


  /**
   * Validar y registrar cliente web (nombre + email)
   */
  async validateWebClient(name: string, email: string): Promise<{ clientUuid: string }> {
    try {
      // console.log('👤 Validando cliente web:', { name, email });

      const response = await fetch(`${this.baseUrl}/clients/validate-web-client`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, email })
      });


      const result = await response.json();


      // Si el email ya existe, retorna 202
      // if (response.status === 202) {
      //   const error = new Error('Email ya registrado');
      //   (error as any).needsOTP = true;
      //   throw error;
      // }

      if (response.status === 202 || result.code === 202) {
        // console.log('⚠️ Email ya registrado, requiere OTP');
        const error = new Error('Email ya registrado');
        (error as any).needsOTP = true;
        (error as any).email = email;
        throw error;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // console.log('✅ Cliente validado:', result);

      return {
        clientUuid: result.data?.clientUuid || result.clientUuid
      };

    } catch (error) {
      console.error('❌ Error validando cliente:', error);
      throw error;
    }
  }

  /**
   * Verificar código OTP para recuperar acceso
   */
  async verifyOTP(email: string, otpCode: string): Promise<{ clientUuid: string }> {
    try {
      // console.log('🔐 Verificando código OTP para:', email);

      const response = await fetch(`${this.baseUrl}/otp/web`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          otpCode: otpCode
        })
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 400) {
          throw new Error('Código OTP inválido o expirado');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      // console.log('✅ OTP verificado correctamente:', result);

      return {
        clientUuid: result.data?.clientUuid || result.clientUuid
      };

    } catch (error) {
      console.error('❌ Error verificando OTP:', error);
      throw error;
    }
  }


  /**
   * Reenviar código OTP - ESTO ESTÁ PENDIENTE
   */
  async resendOTP(email: string): Promise<boolean> {
    try {
      // console.log('📧 Reenviando código OTP a:', email);

      const response = await fetch(`${this.baseUrl}/otp/web/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // console.log('✅ Código OTP reenviado');
      return true;

    } catch (error) {
      console.error('❌ Error reenviando OTP:', error);
      throw error;
    }
  }



}
